import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Search, FileText, Eye, Paperclip, ChevronLeft, ChevronRight, Download, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'HOLD', label: 'On Hold' },
  { value: 'MANAGER_APPROVED', label: 'Manager Approved' },
  { value: 'MANAGER_REJECTED', label: 'Manager Rejected' },
  { value: 'PENDING_ADDITIONAL_APPROVAL', label: 'Pending 2nd Manager' },
  { value: 'ADDITIONAL_MANAGER_APPROVED', label: '2nd Manager Approved' },
  { value: 'ADDITIONAL_MANAGER_REJECTED', label: '2nd Manager Rejected' },
  { value: 'INTERNAL_AUDIT_APPROVED', label: 'Audit Approved' },
  { value: 'INTERNAL_AUDIT_REJECTED', label: 'Audit Rejected' },
  { value: 'ACCOUNTS_APPROVED', label: 'Accounts Approved' },
  { value: 'ACCOUNTS_REJECTED', label: 'Accounts Rejected' },
  { value: 'PAID', label: 'Paid' },
];

export const InternalAuditAllVouchersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      // Use getAllForAudit to get ALL vouchers without role-based filtering
      const data = await vouchersApi.getAllForAudit();
      setVouchers(data);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  // Filter vouchers
  const filteredVouchers = vouchers.filter((v) => {
    const matchesSearch =
      v.beneficiaryName?.toLowerCase().includes(search.toLowerCase()) ||
      v.voucherNo?.toLowerCase().includes(search.toLowerCase()) ||
      v.poNumber?.toLowerCase().includes(search.toLowerCase()) ||
      v.department?.toLowerCase().includes(search.toLowerCase()) ||
      v.createdByName?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Export to CSV function
  const handleExportCSV = () => {
    if (filteredVouchers.length === 0) {
      toast.error('No vouchers to export');
      return;
    }

    const headers = ['Voucher No', 'PO Number', 'Date', 'Employee', 'Department', 'Beneficiary', 'Amount', 'Currency', 'Payment Method', 'Status'];
    
    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      // If the value contains comma, quote, or newline, wrap in quotes and escape existing quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    const csvData = filteredVouchers.map(v => [
      escapeCSV(v.voucherNo || 'Draft'),
      escapeCSV(v.poNumber || ''),
      escapeCSV(formatDate(v.date)),
      escapeCSV(v.createdByName || ''),
      escapeCSV(v.department || ''),
      escapeCSV(v.beneficiaryName || ''),
      escapeCSV(v.amountTotal || 0),
      escapeCSV(v.currency || 'SAR'),
      escapeCSV(v.paymentMethod || ''),
      escapeCSV(v.status || '')
    ]);

    // Add UTF-8 BOM for Excel compatibility with special characters
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_vouchers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV exported successfully');
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredVouchers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVouchers = filteredVouchers.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-64 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="internal-audit-all-vouchers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">All Vouchers</h1>
          <p className="text-muted-foreground text-sm mt-1">
            View and audit all vouchers in the system
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={filteredVouchers.length === 0}
          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
          data-testid="export-csv-button"
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#6a5da4]" />
              All Vouchers ({filteredVouchers.length})
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by voucher no, PO number, beneficiary..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredVouchers.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">No vouchers found</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'No vouchers available'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Voucher No</TableHead>
                      <TableHead className="font-semibold">PO Number</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Employee</TableHead>
                      <TableHead className="font-semibold">Beneficiary</TableHead>
                      <TableHead className="font-semibold">Amount</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="text-right font-semibold">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedVouchers.map((voucher) => (
                      <TableRow
                        key={voucher.id}
                        className="hover:bg-gray-50"
                        data-testid={`voucher-row-${voucher.id}`}
                      >
                        <TableCell className="font-mono font-medium">
                          <div className="flex items-center gap-1">
                            {voucher.voucherNo || (
                              <span className="text-muted-foreground">Draft</span>
                            )}
                            {voucher.attachments && voucher.attachments.length > 0 && (
                              <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {voucher.poNumber || '-'}
                        </TableCell>
                        <TableCell>{formatDate(voucher.date)}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{voucher.createdByName}</p>
                            <p className="text-xs text-muted-foreground">{voucher.department}</p>
                          </div>
                        </TableCell>
                        <TableCell>{voucher.beneficiaryName}</TableCell>
                        <TableCell className="font-mono">
                          {formatCurrency(voucher.amountTotal, voucher.currency)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={voucher.status} size="sm" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/vouchers/${voucher.id}`)}
                            title="View (Read Only)"
                            data-testid={`view-voucher-${voucher.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {paginatedVouchers.map((voucher) => (
                  <div
                    key={voucher.id}
                    className="bg-white border rounded-lg p-4 shadow-sm"
                    onClick={() => navigate(`/vouchers/${voucher.id}`)}
                    data-testid={`voucher-card-${voucher.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-mono font-semibold text-[#6a5da4]">
                          {voucher.voucherNo || 'Draft'}
                          {voucher.attachments && voucher.attachments.length > 0 && (
                            <Paperclip className="w-3.5 h-3.5 inline ml-1 text-[#6a5da4]" />
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(voucher.date)}</p>
                      </div>
                      <StatusBadge status={voucher.status} size="sm" />
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground">By: {voucher.createdByName}</p>
                      <p className="font-medium text-sm">{voucher.beneficiaryName}</p>
                      <p className="text-xs text-muted-foreground">{voucher.department}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-mono font-semibold">
                        {formatCurrency(voucher.amountTotal, voucher.currency)}
                      </p>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredVouchers.length)} of {filteredVouchers.length} vouchers
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, index, arr) => (
                          <React.Fragment key={page}>
                            {index > 0 && arr[index - 1] !== page - 1 && (
                              <span className="px-2 text-muted-foreground">...</span>
                            )}
                            <Button
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-[#6a5da4] hover:bg-[#5a4d94]' : ''}`}
                              onClick={() => setCurrentPage(page)}
                              data-testid={`pagination-page-${page}`}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      data-testid="pagination-next"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InternalAuditAllVouchersPage;
